import { type FC } from "react";

import styled from "@emotion/styled";
import { DateRangePicker, Colors, Checkbox, Tag } from "@snapchat/snap-design-system";
import { produce } from "immer";

import { useFeatureEnabled } from "../../../common/features/useFeature";
import { checkboxStyle } from "../../../common/styles/Checkbox.styles";
import { type RangeModifier } from "../../../connections/models";
import { Flexbox } from "../../../core/components";
import { toUTCDate } from "../../../core/models/DateExtensions";

interface ScheduleDatePickerProps {
    range: RangeModifier;
    onChange: (newValue: RangeModifier) => void;
    initialMonth?: Date;
}

const AdditionalOptions = styled.div`
    padding: 0 16px 16px;
    background-color: ${Colors.WHITE};
`;

export const ScheduleDatePicker: FC<ScheduleDatePickerProps> = ({ range, onChange, initialMonth }) => {
    const useClientUserTimezoneEnabled = useFeatureEnabled("UseClientUserTimezone");
    const handleUseClientTimezone = () => {
        onChange(
            produce(range, (draft) => {
                draft.useClientUserLocalTimezone = !range.useClientUserLocalTimezone;
            }),
        );
    };

    return (
        <Flexbox flexDirection="column">
            <DateRangePicker
                value={range}
                // @ts-ignore
                onChange={onChange}
                initialMonth={initialMonth}
                today={toUTCDate(new Date(), false)}
            />
            <AdditionalOptions>
                {useClientUserTimezoneEnabled ? (
                    <Flexbox justifyContent="space-between">
                        <Flexbox alignItems="baseline" onClick={handleUseClientTimezone}>
                            <Checkbox
                                label="Use client user local timezone"
                                checkboxClassName={checkboxStyle}
                                value={range.useClientUserLocalTimezone}
                            />
                        </Flexbox>
                        <Tag status="info" size="small">
                            Time Zone: {range.useClientUserLocalTimezone ? "Client" : "UTC +0"}
                        </Tag>
                    </Flexbox>
                ) : (
                    <Tag status="info" size="small">
                        Time Zone: UTC +0
                    </Tag>
                )}
            </AdditionalOptions>
        </Flexbox>
    );
};
